import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { XIcon, AlertCircleIcon, HeartPulseIcon, BellIcon, ShieldCheckIcon, PersonStandingIcon } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: ComponentType<{
    className?: string;
  }>;
}
interface AlertsSheetProps {
  room: Room;
  onClose: () => void;
}
function isBedroom(room: Room): boolean {
  const name = room.name.toLowerCase();
  const id = room.id.toLowerCase();
  return id.includes('bedroom') || name.includes('안방') || name.includes('침실');
}
const ROOM_ALERTS: Record<string, string[]> = {
  living: [],
  bedroom: ['온도가 설정보다 높습니다', '에어컨 효율 저하 감지'],
  kitchen: ['이산화탄소 농도 증가'],
  master_bedroom: ['습도가 다소 높습니다'],
  bathroom: [],
  bathroom2: [],
  bedroom2: [],
  dressroom: [],
  outdoor_unit: [],
  entrance: [],
  alpha_room: []
};
export function AlertsSheet({
  room,
  onClose
}: AlertsSheetProps) {
  const alerts = ROOM_ALERTS[room.id] || [];
  const isBedroomRoom = isBedroom(room);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-end justify-center" onClick={onClose} data-id="element-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-51" />

      {/* Sheet */}
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 35
    }} onClick={e => e.stopPropagation()} className="relative z-10 w-full max-w-md bg-slate-900/95 backdrop-blur-xl border-t border-white/10 rounded-t-2xl overflow-hidden max-h-[70vh]" data-id="element-52">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1" data-id="element-53">
          <div className="w-10 h-1 rounded-full bg-white/20" data-id="element-54" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5" data-id="element-55">
          <div className="flex items-center gap-2.5" data-id="element-56">
            <div className="p-1.5 rounded-lg bg-amber-500/20" data-id="element-57">
              <BellIcon className="w-4 h-4 text-amber-400" data-id="element-58" />
            </div>
            <div data-id="element-59">
              <h2 className="text-sm font-bold text-slate-200" data-id="element-60">
                알림 · {room.name}
              </h2>
              <p className="text-[10px] text-slate-500 font-mono" data-id="element-61">
                시스템 알림 및 시니어케어
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg active:bg-white/5 text-slate-500" data-id="element-62">
            <XIcon className="w-5 h-5" data-id="element-63" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto max-h-[50vh]" data-id="element-64">
          {/* Alerts */}
          {alerts.length > 0 ? <div className="space-y-2" data-id="element-65">
              <div className="flex items-center gap-2 text-amber-400 mb-2" data-id="element-66">
                <AlertCircleIcon className="w-4 h-4" data-id="element-67" />
                <span className="text-xs font-bold uppercase tracking-wider" data-id="element-68">
                  시스템 알림
                </span>
              </div>
              {alerts.map((alert, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.05
          }} className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20" data-id="element-69">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" data-id="element-70" />
                  <span className="text-sm text-slate-300" data-id="element-71">{alert}</span>
                </motion.div>)}
            </div> : <div className="flex flex-col items-center py-6 gap-2" data-id="element-72">
              <ShieldCheckIcon className="w-8 h-8 text-emerald-500/50" data-id="element-73" />
              <span className="text-sm text-slate-500" data-id="element-74">알림이 없습니다</span>
            </div>}

          {/* Senior Care Section */}
          {isBedroomRoom && <div className="space-y-2 pt-2 border-t border-white/5" data-id="element-75">
              <div className="flex items-center gap-2 text-rose-400 mb-2" data-id="element-76">
                <HeartPulseIcon className="w-4 h-4" data-id="element-77" />
                <span className="text-xs font-bold uppercase tracking-wider" data-id="element-78">
                  시니어케어
                </span>
              </div>
              <div className="glass-panel rounded-xl p-3.5 space-y-3" data-id="element-79">
                <div className="flex items-center justify-between" data-id="element-80">
                  <span className="text-sm text-slate-300" data-id="element-81">재실 감지</span>
                  <div className="flex items-center gap-1.5 text-emerald-400" data-id="element-82">
                    <PersonStandingIcon className="w-4 h-4" data-id="element-83" />
                    <span className="text-xs font-bold" data-id="element-84">감지됨</span>
                  </div>
                </div>
                <div className="flex items-center justify-between" data-id="element-85">
                  <span className="text-sm text-slate-300" data-id="element-86">심박수</span>
                  <span className="text-xs font-mono font-bold text-rose-400" data-id="element-87">
                    72 bpm · 정상
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-88">
                  <span className="text-sm text-slate-300" data-id="element-89">호흡수</span>
                  <span className="text-xs font-mono font-bold text-cyan-400" data-id="element-90">
                    16 rpm · 정상
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-91">
                  <span className="text-sm text-slate-300" data-id="element-92">수면 상태</span>
                  <span className="text-xs font-mono font-bold text-indigo-400" data-id="element-93">
                    깊은 수면
                  </span>
                </div>
              </div>
            </div>}
        </div>

        {/* Bottom safe area */}
        <div className="h-6" data-id="element-94" />
      </motion.div>
    </motion.div>;
}